import { useTranslation } from "react-i18next";
import { WEBSITE_URL, PHONE_NUMBER, EMAIL, ADDRESS, LEGAL_COMPANY } from "@/lib/constants";

const DynamicSchemaMarkup = () => {
  const { t, i18n } = useTranslation();
  const isDe = i18n.language === "de";

  // Product Schema with AggregateOffer — Sprinter 3,5t (Haupt-Asset)
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${WEBSITE_URL}/#product`,
    "name": t("schema.productName"),
    "category": "CommercialTransportRental",
    "description": t("schema.productDescription"),
    "image": `${WEBSITE_URL}/kuehlsprinter-product.png`,
    "brand": { "@type": "Brand", "name": "Frostflotte" },
    "offers": {
      "@type": "AggregateOffer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "lowPrice": "149",
      "highPrice": "2199",
      "offerCount": 4,
      "url": `${WEBSITE_URL}/#preise`
    },
    "areaServed": [
      { "@type": "City", "name": "Frankfurt am Main" },
      { "@type": "City", "name": "Offenbach" },
      { "@type": "City", "name": "Wiesbaden" },
      { "@type": "City", "name": "Mainz" },
      { "@type": "City", "name": "Darmstadt" },
      { "@type": "City", "name": "Hanau" },
      { "@type": "City", "name": "Aschaffenburg" },
      { "@type": "City", "name": "Bad Homburg" },
      { "@type": "AdministrativeArea", "name": "Rhein-Main" },
      { "@type": "AdministrativeArea", "name": "Hessen" },
      { "@type": "AdministrativeArea", "name": "Nordrhein-Westfalen" },
      { "@type": "AdministrativeArea", "name": "Bayern" },
      { "@type": "AdministrativeArea", "name": "Baden-Württemberg" },
      { "@type": "AdministrativeArea", "name": "Rheinland-Pfalz" },
      { "@type": "AdministrativeArea", "name": "Niedersachsen" },
      { "@type": "AdministrativeArea", "name": "Sachsen" },
      { "@type": "City", "name": "Berlin" },
      { "@type": "City", "name": "Hamburg" },
      { "@type": "City", "name": "München" },
      { "@type": "City", "name": "Köln" },
      { "@type": "City", "name": "Stuttgart" },
      { "@type": "Country", "name": isDe ? "Deutschland" : "Germany" }
    ]
  };

  // Product Schema — Mercedes Vito Kühlkastenwagen (Zweitfahrzeug)
  const vitoProductSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "@id": `${WEBSITE_URL}/#product-vito`,
    "name": t("schema.vitoProductName"),
    "alternateName": t("schema.vitoAlternateNames", { returnObjects: true }) as string[],
    "category": "CommercialTransportRental",
    "description": t("schema.vitoProductDescription"),
    "image": `${WEBSITE_URL}/og-image.jpg`,
    "brand": { "@type": "Brand", "name": "Frostflotte" },
    "offers": {
      "@type": "AggregateOffer",
      "availability": "https://schema.org/InStock",
      "priceCurrency": "EUR",
      "lowPrice": "109",
      "highPrice": "1599",
      "offerCount": 4,
      "url": `${WEBSITE_URL}/#preise`
    },
    "areaServed": [
      { "@type": "City", "name": "Frankfurt am Main" },
      { "@type": "City", "name": "Offenbach" },
      { "@type": "City", "name": "Wiesbaden" },
      { "@type": "City", "name": "Mainz" },
      { "@type": "City", "name": "Darmstadt" },
      { "@type": "City", "name": "Hanau" },
      { "@type": "City", "name": "Aschaffenburg" },
      { "@type": "City", "name": "Bad Homburg" },
      { "@type": "AdministrativeArea", "name": "Rhein-Main" },
      { "@type": "AdministrativeArea", "name": "Hessen" },
      { "@type": "AdministrativeArea", "name": "Nordrhein-Westfalen" },
      { "@type": "AdministrativeArea", "name": "Bayern" },
      { "@type": "AdministrativeArea", "name": "Baden-Württemberg" },
      { "@type": "AdministrativeArea", "name": "Rheinland-Pfalz" },
      { "@type": "AdministrativeArea", "name": "Niedersachsen" },
      { "@type": "AdministrativeArea", "name": "Sachsen" },
      { "@type": "City", "name": "Berlin" },
      { "@type": "City", "name": "Hamburg" },
      { "@type": "City", "name": "München" },
      { "@type": "City", "name": "Köln" },
      { "@type": "City", "name": "Stuttgart" },
      { "@type": "Country", "name": isDe ? "Deutschland" : "Germany" }
    ]
  };

  // LocalBusiness Schema
  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "additionalType": "https://schema.org/AutoRental",
    "@id": `${WEBSITE_URL}/#organization`,
    "name": ADDRESS.company,
    "alternateName": "Frostflotte Kühlfahrzeugvermietung",
    "legalName": LEGAL_COMPANY.name,
    "image": `${WEBSITE_URL}/og-image.jpg`,
    "description": t("schema.localBusinessDescription"),
    "url": WEBSITE_URL,
    "telephone": PHONE_NUMBER,
    "email": EMAIL,
    "priceRange": "€€",
    "paymentAccepted": t("schema.paymentAccepted", { returnObjects: true }) as string[],
    "currenciesAccepted": "EUR",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": ADDRESS.street,
      "addressLocality": "Frankfurt am Main",
      "postalCode": "60487",
      "addressRegion": ADDRESS.region,
      "addressCountry": ADDRESS.country
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "50.1269",
      "longitude": "8.6195"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "07:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "08:00",
        "closes": "14:00"
      }
    ],
    "areaServed": [
      { "@type": "City", "name": "Frankfurt am Main" },
      { "@type": "City", "name": "Offenbach" },
      { "@type": "City", "name": "Wiesbaden" },
      { "@type": "City", "name": "Mainz" },
      { "@type": "City", "name": "Darmstadt" },
      { "@type": "City", "name": "Hanau" },
      { "@type": "City", "name": "Aschaffenburg" },
      { "@type": "City", "name": "Bad Homburg" },
      { "@type": "City", "name": "Friedberg" },
      { "@type": "City", "name": "Rüsselsheim" },
      { "@type": "City", "name": "Hofheim" },
      { "@type": "AdministrativeArea", "name": "Rhein-Main" },
      { "@type": "AdministrativeArea", "name": "Hessen" },
      { "@type": "AdministrativeArea", "name": "Nordrhein-Westfalen" },
      { "@type": "AdministrativeArea", "name": "Bayern" },
      { "@type": "AdministrativeArea", "name": "Baden-Württemberg" },
      { "@type": "AdministrativeArea", "name": "Rheinland-Pfalz" },
      { "@type": "AdministrativeArea", "name": "Niedersachsen" },
      { "@type": "AdministrativeArea", "name": "Sachsen" },
      { "@type": "City", "name": "Berlin" },
      { "@type": "City", "name": "Hamburg" },
      { "@type": "City", "name": "München" },
      { "@type": "City", "name": "Köln" },
      { "@type": "City", "name": "Stuttgart" },
      { "@type": "Country", "name": isDe ? "Deutschland" : "Germany" },
      { "@type": "Place", "name": "Frankfurt-Rödelheim" },
      { "@type": "Place", "name": "Frankfurt-Bockenheim" },
      { "@type": "Place", "name": "Frankfurt-Gallus" }
    ],
    "makesOffer": [
      { "@type": "Offer", "itemOffered": { "@type": "Product", "@id": `${WEBSITE_URL}/#product` } },
      { "@type": "Offer", "itemOffered": { "@type": "Product", "@id": `${WEBSITE_URL}/#product-vito` } }
    ],
    "sameAs": [
      "https://www.instagram.com/frostflotte/",
      "https://www.tiktok.com/@frostflotte"
    ]
  };

  // WebSite Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${WEBSITE_URL}/#website`,
    "url": WEBSITE_URL,
    "name": "Frostflotte",
    "alternateName": t("schema.websiteAlternateName"),
    "description": t("schema.websiteDescription"),
    "publisher": {
      "@id": `${WEBSITE_URL}/#organization`
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(vitoProductSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(websiteSchema) }}
      />
    </>
  );
};

export default DynamicSchemaMarkup;
